import React, { useState } from 'react';
import './CampaignCreation.css';
import { Button, notification, Form, Input } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined } from '@ant-design/icons';
import ThirdStep from './Steps/Step3';
import StepTwo from './Steps/Step2';
import StepFour from './Steps/Step4';
import StepFive from './Steps/Step5';
import CampaignLaunchConfirmation from './CampaignLaunchConfirmation';
import CampaignLaunchLoading from './CampaignLaunchLoading';
import StepOne from './Steps/Step1';
import { useMutation } from '@tanstack/react-query';
import { createCampaignAI } from '@/api/novi/campaigns';
import { createSchedule } from '@/api/novi/schedule';
import { AICampaignFormData } from './types';
import { createUser } from '@/api/user';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import useAuth from '@/routes/hooks/useAuth';
import useResellerAuth from '@/routes/hooks/useResellerAuth';

interface LeadField {
	id: string;
	name: string;
	required: boolean;
	example: string;
}

interface CampaignCreationProps {
	setShowCreateCampaign: React.Dispatch<React.SetStateAction<boolean>>;
	refetch: () => void;
	campaign_user_email: string;
	campaign_user_name: string;
	handleBack: () => void;
	handleShowCampaign?: (id: string) => void;
	modalView?: boolean;
	clientType: 'Reseller' | 'Client';
	userId?: string;
	resellerView?: boolean;
}

type DayName = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';

const CampaignCreation: React.FC<CampaignCreationProps> = ({
	refetch,
	campaign_user_email,
	campaign_user_name,
	handleBack,
	handleShowCampaign,
	modalView = false,
	clientType,
	userId = null,
	resellerView = false,
}) => {
	const [currentStep, setCurrentStep] = useState<number>(1);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isLaunched, setIsLaunched] = useState<boolean>(false);
	const [campaignId, setCampaignId] = useState<string>('');
	const totalSteps = resellerView ? 6 : 5;
	const currentReseller = useAppSelector(getResellerData);
	const [form] = Form.useForm();
	const { refetch: refetchUser } = useAuth();
	const { refetch: refetchReseller } = useResellerAuth();

	// Loading state tracking
	const [campaignStatus, setCampaignStatus] = useState({
		isLoading: false,
		progress: 0,
		error: '',
	});

	const [scheduleStatus, setScheduleStatus] = useState({
		isLoading: false,
		progress: 0,
		error: '',
	});

	const [newFieldName, setNewFieldName] = React.useState<string>('');

	const [formData, setFormData] = useState<AICampaignFormData>({
		name: '',
		campaignGoal: '',
		campaignType: '',
		timezone: '',
		scheduleType: 'business',
		leadSourceType: 'zapier',
		maxCallsPerDay: 25,
		selectedDays: [],
		startTime: '09:00',
		endTime: '18:00',
		hoursBetweenRetries: 0,
		maximumRetries: 0,
		leadFields: [
			{ id: 'firstName', name: 'First Name', required: true, example: 'John' },
			{ id: 'lastName', name: 'Last Name', required: true, example: 'Smith' },
			{ id: 'email', name: 'Email', required: true, example: 'john@company.com' },
			{ id: 'phoneNumber', name: 'Phone Number', required: true, example: '(555) 123-4567' },
			{ id: 'company', name: 'Company', required: false, example: 'Acme Corp' },
		],
		username: '',
		email: '',
		password: '',
	});

	const { mutate: createNewSchedule } = useMutation({
		mutationKey: ['create-schedule'],
		mutationFn: createSchedule,
		onMutate: () => {
			setScheduleStatus({
				isLoading: true,
				progress: 10,
				error: '',
			});
		},
		onSuccess: () => {
			setScheduleStatus((prev) => ({
				...prev,
				isLoading: false,
				progress: 100,
			}));
		},
		onError: (error) => {
			setScheduleStatus((prev) => ({
				...prev,
				isLoading: false,
				error: error.message,
			}));

			notification.error({
				message: 'Error',
				description: error.message,
			});
		},
	});

	const mutation = useMutation({
		mutationKey: ['createCampaignAI'],
		mutationFn: createCampaignAI,
		onMutate: () => {
			setCampaignStatus({
				isLoading: true,
				progress: 10,
				error: '',
			});
		},
		onSuccess: (data) => {
			refetchUser();
			if (resellerView) {
				refetchReseller();
			}

			// Update campaign progress
			setCampaignStatus((prev) => ({
				...prev,
				progress: 80,
			}));

			setCampaignId(data.data?.campaignId);
			let tempDays: number[] = [];
			if (formData.scheduleType === 'business') {
				tempDays = [1, 2, 3, 4, 5];
			} else if (formData.scheduleType === 'extended') {
				tempDays = [1, 2, 3, 4, 5];
				// startTime=  ''
				// endTime = ''
			} else if (formData.scheduleType === 'custom' && formData.selectedDays?.length) {
				const dayNameToNumber: Record<DayName, number> = {
					Monday: 1,
					Tuesday: 2,
					Wednesday: 3,
					Thursday: 4,
					Friday: 5,
					Saturday: 6,
					Sunday: 7,
				};

				tempDays = formData.selectedDays.map((day) => dayNameToNumber[day as DayName]);
			}

			// Campaign has been created, now create the schedule
			setCampaignStatus((prev) => ({
				...prev,
				progress: 100,
				isLoading: false,
			}));

			createNewSchedule(
				{
					campaign_id: data.data?.campaignId,
					clientType,
					schedule: {
						allowed_retries: formData.maximumRetries || 0,
						time_between_retries: formData.hoursBetweenRetries || 0,
						allowed_times_start: formData.startTime || '',
						allowed_times_end: formData.endTime || '',
						days: tempDays,
						timezone: formData.timezone,
					},
				},
				{
					onSuccess: (scheduleData) => {
						console.log('Schedule created successfully:', scheduleData);
						// Progress update is handled by the schedule mutation

						// Delay refetch a bit to ensure backend has fully processed
						setTimeout(() => {
							refetch();
						}, 1000);
					},
					onError: (error) => {
						console.error('Error creating schedule:', error);
					},
				},
			);
		},
		onError: (error) => {
			setCampaignStatus((prev) => ({
				...prev,
				isLoading: false,
				error: error.message,
			}));

			console.error('Error creating campaign AI:', error);
		},
	});

	const createUserMutation = useMutation({
		mutationKey: ['create-user'],
		mutationFn: createUser,
		onError: (error: any) => {
			notification.error({
				message: 'Registration Failed',
				description: error.message || 'Failed to save progress. Please try again.',
			});
		},
	});

	// Progress updates during API calls
	React.useEffect(() => {
		let interval: NodeJS.Timeout;

		if (campaignStatus.isLoading && campaignStatus.progress < 80) {
			interval = setInterval(() => {
				setCampaignStatus((prev) => ({
					...prev,
					progress: Math.min(prev.progress + 5, 80),
				}));
			}, 500);
		}

		return () => {
			if (interval) clearInterval(interval);
		};
	}, [campaignStatus.isLoading, campaignStatus.progress]);

	React.useEffect(() => {
		let interval: NodeJS.Timeout;

		if (scheduleStatus.isLoading && scheduleStatus.progress < 90) {
			interval = setInterval(() => {
				setScheduleStatus((prev) => ({
					...prev,
					progress: Math.min(prev.progress + 7, 90),
				}));
			}, 700);
		}

		return () => {
			if (interval) clearInterval(interval);
		};
	}, [scheduleStatus.isLoading, scheduleStatus.progress]);

	const addNewField = () => {
		if (!newFieldName.trim()) return;

		const newId = `custom_${Date.now()}`;
		const newField: LeadField = {
			id: newId,
			name: newFieldName,
			required: false,
			example: '',
		};
		let leadFields = formData.leadFields || [];
		leadFields.push(newField);
		setFormData((prevData) => ({
			...prevData,
			leadFields: leadFields,
		}));
		setNewFieldName('');
	};

	const removeField = (id: string) => {
		const updatedFields = (formData.leadFields || []).filter((field) => field.id !== id);
		setFormData((prevData) => ({
			...prevData,
			leadFields: updatedFields,
		}));
	};

	const handleFieldChange = (id: string, value: string) => {
		let leadFields = formData.leadFields || [];
		setFormData((prevData) => ({
			...prevData,
			leadFields: leadFields.map((field) => (field.id === id ? { ...field, name: value } : field)),
		}));
	};

	const toggleRequired = (id: string) => {
		let leadFields = formData.leadFields || [];
		setFormData((prevData) => ({
			...prevData,
			leadFields: leadFields.map((field) =>
				field.id === id ? { ...field, required: !field.required } : field,
			),
		}));
	};

	const progressPercentage = (currentStep / totalSteps) * 100;

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});
	};

	const handleRadioChange = (name: keyof AICampaignFormData, value: string) => {
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prevData) => ({
			...prevData,
			maxCallsPerDay: parseInt(e.target.value, 10),
		}));
	};

	const handleTimeZoneChange = (value: string) => {
		console.log(value);
		setFormData((prevData) => ({
			...prevData,
			timezone: value,
		}));
	};

	const handleDaySelection = (selectedDays: string[]) => {
		setFormData((prevData) => ({
			...prevData,
			selectedDays,
		}));
	};

	const goToNextStep = () => {
		if (currentStep === 1 && !formData.name) {
			notification.error({
				message: 'Error',
				description: 'Please provide a campaign name.',
			});
			return;
		}
		if (currentStep === 1 && !formData.campaignGoal) {
			notification.error({
				message: 'Error',
				description: 'Please provide a campaign goal.',
			});
			return;
		}
		if (currentStep === 2 && !formData.campaignType) {
			notification.error({
				message: 'Error',
				description: 'Please select a campaign type.',
			});
			return;
		}
		if (currentStep === 4 && !formData.leadSourceType) {
			notification.error({
				message: 'Error',
				description: 'Please select a lead source type.',
			});
			return;
		}
		if (resellerView && currentStep === 5) {
			form
				.validateFields()
				.then((values) => {
					console.log('Form values:', values); // Debug log
					setFormData((prev) => ({
						...prev,
						username: values.username,
						email: values.email,
						password: values.password,
					}));
					setCurrentStep(currentStep + 1);
				})
				.catch((error) => {
					console.error('Validation failed:', error);
					notification.error({
						message: 'Validation Error',
						description: 'Please fill in all required fields correctly.',
					});
				});
			return;
		}

		if (currentStep < totalSteps) {
			setCurrentStep(currentStep + 1);
		}
	};

	const goToPreviousStep = () => {
		if (currentStep > 1) {
			setCurrentStep(currentStep - 1);
		}
	};

	const handleLaunchCampaign = async () => {
		if (currentStep === 5 && !formData.scheduleType) {
			notification.error({
				message: 'Error',
				description: 'Please select a schedule type.',
			});
			return;
		}
		if (currentStep === 5 && formData.scheduleType === 'custom' && !formData.selectedDays?.length) {
			notification.error({
				message: 'Error',
				description: 'Please select at least one day of the week.',
			});
			return;
		}
		if (currentStep === 5 && !formData.timezone) {
			notification.error({
				message: 'Error',
				description: 'Please select a timezone.',
			});
			return;
		}
		if (currentStep === 5 && formData.scheduleType === 'custom' && !formData.startTime) {
			notification.error({
				message: 'Error',
				description: 'Please select a start time.',
			});
			return;
		}
		if (currentStep === 5 && formData.scheduleType === 'custom' && !formData.endTime) {
			notification.error({
				message: 'Error',
				description: 'Please select an end time.',
			});
			return;
		}
		if (
			currentStep === 5 &&
			formData.scheduleType === 'custom' &&
			(formData.startTime ?? 0) >= (formData.endTime ?? 0)
		) {
			notification.error({
				message: 'Error',
				description: 'Start time must be before end time.',
			});
			return;
		}
		if (currentStep === 5 && (formData.hoursBetweenRetries ?? 0) < 0) {
			notification.error({
				message: 'Error',
				description: 'Hours between retries must be a positive number.',
			});
			return;
		}
		if (currentStep === 5 && (formData.maximumRetries ?? 0) < 0) {
			notification.error({
				message: 'Error',
				description: 'Maximum retries must be a positive number.',
			});
			return;
		}
		if (resellerView && (!formData.username || !formData.email || !formData.password)) {
			notification.error({
				message: 'Error',
				description: 'Please complete the user registration form.',
			});
			return;
		}
		setIsLoading(true);

		try {
			let userIdToUse = null;
			let createUserResponse = null;
			if (userId) {
				userIdToUse = userId;
			} else {
				if (resellerView) {
					console.log('Creating user with data:', {
						// Debug log
						username: formData.username,
						email: formData.email,
						password: formData.password,
					});

					createUserResponse = await createUserMutation.mutateAsync({
						username: formData.username as string,
						emailAddress: formData.email as string,
						password: formData.password as string,
						type: 'SubAccount',
						resellerId: currentReseller?.id || undefined,
						subAccountType: 'Outbound',
					});

					console.log('User creation response:', createUserResponse); // Debug log

					if (createUserResponse.success && createUserResponse?.data?.id) {
						userIdToUse = createUserResponse?.data?.id;
					} else {
						throw new Error('Failed to create user account');
					}
				} else {
					return;
				}
			}

			mutation.mutate({
				businessName: campaign_user_name,
				username: campaign_user_name,
				name: formData.name,
				campaignGoal: formData.campaignGoal,
				communicationType: formData.campaignType,
				leadSource: formData.leadSourceType,
				fieldToCollect: formData?.leadFields ?? [],
				userId: userIdToUse as string,
				clientType: resellerView ? 'Reseller' : 'Client',
				email: resellerView ? (formData.email as string) : campaign_user_email,
			});
		} catch (error) {
			console.error('Error launching campaign:', error);
			notification.error({
				message: 'Error',
				description: 'Failed to launch campaign. Please try again.',
			});
			setIsLoading(false);
		}
	};

	const handleLoadingComplete = () => {
		setIsLoading(false);
		setIsLaunched(true);
	};

	const Step1 = (
		<StepOne formData={formData} setFormData={setFormData} handleInputChange={handleInputChange} />
	);

	const Step2 = (
		<StepTwo formData={formData} setFormData={setFormData} handleInputChange={handleInputChange} />
	);

	const Step3 = (
		<ThirdStep
			formData={formData}
			setFormData={setFormData}
			newFieldName={newFieldName}
			setNewFieldName={setNewFieldName}
			handleFieldChange={handleFieldChange}
			toggleRequired={toggleRequired}
			addNewField={addNewField}
			removeField={removeField}
		/>
	);
	const Step4 = <StepFour formData={formData} handleRadioChange={handleRadioChange} />;
	const Step5 = (
		<StepFive
			formData={formData}
			handleInputChange={handleInputChange}
			handleRadioChange={handleRadioChange}
			handleSliderChange={handleSliderChange}
			handleDaySelection={handleDaySelection}
			handleTimeZoneChange={handleTimeZoneChange}
		/>
	);

	const Step6 = (
		<div className="user-registration-step">
			<h2>Create User Account</h2>
			<Form
				form={form}
				layout="vertical"
				initialValues={{
					username: formData.username || '',
					email: formData.email || '',
					password: formData.password || '',
				}}
				onValuesChange={(changedValues) => {
					console.log('Form values changed:', changedValues); // Debug log
					setFormData((prev) => ({
						...prev,
						...changedValues,
					}));
				}}
			>
				<Form.Item
					name="username"
					label="Username"
					rules={[
						{ required: true, message: 'Please enter a username' },
						{ pattern: /^[a-zA-Z0-9]+$/, message: 'Username can only contain letters and numbers' },
					]}
				>
					<Input placeholder="Enter username" />
				</Form.Item>
				<Form.Item
					name="email"
					label="Email"
					rules={[
						{ required: true, message: 'Please enter an email' },
						{ type: 'email', message: 'Please enter a valid email' },
					]}
				>
					<Input placeholder="Enter email" />
				</Form.Item>
				<Form.Item
					name="password"
					label="Password"
					rules={[
						{ required: true, message: 'Please enter a password' },
						{ min: 8, message: 'Password must be at least 8 characters' },
					]}
				>
					<Input.Password placeholder="Enter password" />
				</Form.Item>
			</Form>
		</div>
	);

	const steps = [
		{ id: 1, title: 'Campaign Goal', component: Step1 },
		{ id: 2, title: 'Campaign Type', component: Step2 },
		{ id: 3, title: 'Target Audience', component: Step3 },
		{ id: 4, title: 'Message Content', component: Step4 },
		{ id: 5, title: 'Schedule', component: Step5 },
		...(resellerView ? [{ id: 6, title: 'User Registration', component: Step6 }] : []),
	];

	const currentStepContent = steps.find((step) => step.id === currentStep)?.component;

	if (isLoading) {
		return (
			<CampaignLaunchLoading
				onFinish={handleLoadingComplete}
				campaignStatus={campaignStatus}
				scheduleStatus={scheduleStatus}
				handleBack={handleBack}
			/>
		);
	}

	if (isLaunched) {
		return (
			<CampaignLaunchConfirmation
				formData={formData}
				campaignId={campaignId}
				onBackToDashboard={handleBack}
				onViewCampaign={() => (handleShowCampaign ? handleShowCampaign(campaignId) : null)}
			/>
		);
	}

	return (
		<div className="multi-step-form-container">
			<div className="progress-bar-container">
				<div className="progress-bar" style={{ width: `${progressPercentage}%` }}></div>
			</div>

			<div className="text-end">
				<p>
					Step {currentStep} of {totalSteps}
				</p>
			</div>

			<div className="form-step">{currentStepContent}</div>

			<div className={`mt-4 flex justify-between flex-wrap gap-3`}>
				{currentStep == 1 && !modalView && (
					<Button
						onClick={handleBack}
						className="novi-back-button"
						icon={<ArrowLeftOutlined />}
						size="large"
					>
						Back to Dashboard
					</Button>
				)}
				{currentStep > 1 && (
					<Button
						onClick={goToPreviousStep}
						className="novi-back-button"
						icon={<ArrowLeftOutlined />}
						size="large"
					>
						Previous
					</Button>
				)}
				{currentStep < totalSteps ? (
					<Button
						onClick={goToNextStep}
						className={`next-button ${modalView && currentStep === 1 ? 'w-full' : ''}`}
						type="primary"
						size="large"
					>
						Next Step <ArrowRightOutlined />
					</Button>
				) : (
					<Button
						onClick={handleLaunchCampaign}
						className="launch-button"
						type="primary"
						size="large"
						style={{ backgroundColor: 'green', borderColor: 'green' }}
						loading={campaignStatus.isLoading || scheduleStatus.isLoading}
					>
						<CheckOutlined /> Launch Campaign
					</Button>
				)}
			</div>
		</div>
	);
};

export default CampaignCreation;
